import {
  AppBar,
  Box,
  Button,
  Divider,
  Toolbar,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { useEffect, useState } from "react";
import { RequestError, useApi } from "../services";
import { useNavigate, useParams } from "react-router-dom";
import logoHbr from "../assets/logo-hbr.svg";
import { Barragens } from "../components/home";
import { EditCompanieModal, ErrorOnDialog, HidroDXEditor, LoadingModal } from "../components";
import EditIcon from '@mui/icons-material/Edit';
import { useUserContext } from "../context";


export interface CompanyData {
  id: string;
  nome: string;
  logo?: string;
  banner?: string;
  cnpj: string;
  sede: string;
  phone: string;
  descricao: string;
  municipio: number;
  barragens: number[];
}

const onServicos = () => window.open('https://www.hidrobr.com/areas-de-atuacao/', '_blank');
const onContato = () => window.open('https://www.hidrobr.com/contato/', '_blank');
const onSobre = () => window.open('https://www.hidrobr.com/sobre/', '_blank');

export const HomePage: React.FC = () => {
  const appBarHeight = 76;

  const api = useApi();
  const user = useUserContext();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string; }>();

  const theme = useTheme();
  const contrastText = theme.palette.getContrastText(theme.palette.background.paper);

  const [companyData, setCompanyData] = useState<CompanyData | undefined>(undefined);
  const [companyError, setCompanyError] = useState<RequestError | undefined>(undefined);
  const [openEdit, setOpenEdit] = useState<boolean>(false);

  // Redirecionando para HBR
  useEffect(() => {
    if (!user || "error" in user || id) return;

    if (user.empresa && user.empresa.nome == "HIDROBR") {
      navigate(`/app/hbr`);
      return;
    }
    window.location.href = `/app/home/${user.empreendimento}`;
  }, [user]);

  useEffect(() => {
    if (!id) return;
    api.company.company(id).then((company) => {
      if ('error' in company) {
        setCompanyError(company);
        return;
      }
      setCompanyData(company);
    });
  }, []);

  if (!user) return <LoadingModal open={true} />;

  return (
    <>
      <ErrorOnDialog
        error={companyError?.error ?? "Erro"}
        message={companyError?.message ?? "Erro ao obter dados"}
        open={Boolean(companyError)}
        onClose={() => setCompanyError(undefined)}
      />

      {openEdit && <EditCompanieModal
        showPhone
        open={openEdit}
        initialCompany={companyData}
        onCancel={() => setOpenEdit(false)}
        onCompany={(company) => {
          setOpenEdit(false);
          setCompanyData(company);
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }}
      />}

      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <AppBar sx={{
          display: "flex",
          justifyContent: 'center',
          height: `${appBarHeight}px`,
          backgroundColor: "background.paper",
        }}>
          <Toolbar>
            <img src={companyData?.logo} style={{ height: "38px" }} />
            <div style={{ width: "25px" }} />

            {companyData?.nome && <Typography
              fontWeight={"bold"}
              fontSize={"1.5em"}
              style={{ color: theme.palette.primary.main }}>
              {companyData?.nome.toUpperCase()}
            </Typography>}

            {!("error" in user) && user?.admin && (
              <>
                <div style={{ width: "25px" }} />
                <Tooltip title={"Editar dados do Empreendimento"}>
                  <Button
                    onClick={() => setOpenEdit(true)}
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                  >
                    Editar
                  </Button>
                </Tooltip>
              </>
            )}

            <div style={{ flex: 1 }} />

            <Tooltip title={"Serviços da HIDROBR"}>
              <Button onClick={onServicos}>SERVIÇOS</Button>
            </Tooltip>
            <div style={{ width: "15px" }} />

            <Tooltip title={"Entre em contato com a HIDROBR"}>
              <Button onClick={onContato}>CONTATO</Button>
            </Tooltip>
            <div style={{ width: "15px" }} />

            <Tooltip title={"Sobre a HIDROBR"}>
              <Button onClick={onSobre}>SOBRE</Button>
            </Tooltip>
            <div style={{ width: "15px" }} />

            <img src={logoHbr} onClick={onSobre} style={{ height: "56px", cursor: "pointer" }} />
          </Toolbar>
        </AppBar>

        <Box sx={{ overflowY: "auto" }}>
          <div style={{ height: `${appBarHeight}px` }} />

          {/* Imagem */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '35vw',
              maxHeight: "60vh",
            }}>

            {/* Banner */}
            {companyData?.banner && <img src={companyData?.banner} style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              objectFit: 'cover',
              zIndex: 0,
            }} />}

            {/* Nome */}
            <Typography
              fontSize={"2em"}
              fontWeight={"bold"}
              color="background.paper"
              style={{ textShadow: `2px 2px 4px ${contrastText}` }}
              sx={{
                zIndex: 1000,
                top: "5%",
                left: "2%",
                position: 'absolute',
              }}
            >
              APRESENTAÇÃO:
            </Typography>

            <div style={{ height: "1em" }} />

            <Typography
              fontSize={"2.5em"}
              fontWeight={"bold"}
              color="background.paper"
              style={{ textShadow: `2px 2px 4px ${contrastText}` }}
              sx={{
                zIndex: 1000,
                top: "20%",
                left: "2%",
                position: 'absolute',
              }}
            >
              {companyData?.nome.toUpperCase()}
            </Typography>
          </Box>

          {/* Conteúdo da Página */}
          <Box
            sx={{
              paddingRight: "15px",
              paddingLeft: "15px",
            }}
          >
            {/* Sobre a empresa */}
            {companyData?.descricao &&
              <Box sx={{ padding: "1em", textAlign: 'justify' }}>
                <HidroDXEditor
                  readOnly
                  startText={companyData.descricao}
                />
              </Box>}
            {companyData?.descricao &&
              <Divider sx={{
                marginX: "2em",
                marginBottom: "15px",
              }} />
            }

            {/* Dados das Barragens */}
            <Box sx={{
              width: "100%",
              marginRight: "15px",
            }}>
              {id && companyData?.barragens &&
                <Barragens barragens={companyData?.barragens} />
              }
            </Box>
          </Box>

          {/* Footer */}
          <Toolbar sx={{ backgroundColor: theme.palette.primary.main }}>
            <div style={{ flex: 1 }} />

            <Typography
              color={theme.palette.getContrastText(theme.palette.primary.main)}
              fontWeight={"bold"}
              fontSize={12}
            >
              POWERED BY
            </Typography>

            <Typography
              color={theme.palette.secondary.main}
              fontWeight={"bold"}
              fontSize={12}
            >
              &nbsp;HIDROBR
            </Typography>

          </Toolbar>

        </Box>
      </Box>
    </>
  );
};
